import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { API, TemplateGroupDTO, TemplateSourceDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { QueryParamService } from '../../../services/lists/query-params';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { ErrorService } from '../../../services/error.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'show';

@Component({
  selector: 'sb-page-template-source',
  templateUrl: './template-source.component.html',
  styleUrls: ['./template-source.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateSourceComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly util = inject(UtilService);
  public readonly queryParamService = inject(QueryParamService);
  public readonly modalService = inject(ModalService);
  public readonly translate = inject(TranslateService);
  public readonly modal = inject(ModalNavigationService);
  public readonly errorHandler = inject(ErrorService);
  public readonly permissionService = inject(PermissionService);
  protected readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // global variables
  isInModalContext: boolean;

  // table wrapper variables
  templateSources: TemplateSourceDTO[] = [];

  // filter variables
  templateGroupId: number = -1;
  templateGroups: TemplateGroupDTO[] = [];
  filteredTemplateGroups: TemplateGroupDTO[] = [];
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit(): void {
    const tID = this.queryParamService.getQueryParam('templateGroupID');
    if (tID) {
      this.templateGroupId = +tID;
      this.api
        .mm_templating_provider_TemplateGroup_retrieveById(this.templateGroupId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (group: TemplateGroupDTO) => {
            this.filteredTemplateGroups = [group];
            this.queryParamService.clearQueryParam('templateGroupID');
          },
        });
    }
    if (this.isInModalContext) {
      this.templateSources = this.modal.getCurrentNavigation().data.templateSources;
      this.loading.set(false);
    }
  }

  refreshFromServer() {
    if (this.isInModalContext) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .mm_templating_provider_TemplateSource_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          template_group_id: this.util.arrayToIdArray(this.filteredTemplateGroups),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (templateSources: TemplateSourceDTO[]) => {
          this.templateSources = this.buildSplitButtons(templateSources);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  selectTemplateSource(group: TemplateGroupDTO) {
    if (this.isInModalContext) {
      this.modal.result(group);
    }
  }

  createTemplateSource() {
    if (this.templateGroupId !== -1) {
      this.util.navService.navigateToPoint(this.util.RoutingID.templateSourceCreate, null, {
        queryParams: {
          returnTo: this.util.getPageLink(),
          templateGroupID: this.templateGroupId,
        },
      });
    } else {
      this.util.navService.navigateToPoint(this.util.RoutingID.templateSourceCreate, null, {
        queryParams: { returnTo: this.util.getPageLink() },
      });
    }
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_source_entry_list])) {
        item.sbButtons.push({
          icon: 'templateEntry',
          tooltip: 'lang.globals.showEntries',
          link: RoutingID.templateSourceEntries,
          pathParams: [{ id: 'templateSourceID', value: item?.id }],
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.filteredTemplateGroups = filterMap.get('templateGroups');
  }
}
